import React, { useEffect, useMemo, useState } from "react";

const METRO_CITY_KEYWORDS = [
  "mumbai",
  "delhi",
  "new delhi",
  "bangalore",
  "bengaluru",
  "hyderabad",
  "chennai",
  "kolkata",
  "pune",
  "ahmedabad",
];

const isMetroCityOption = (label) => {
  const text = String(label || "").toLowerCase();
  return METRO_CITY_KEYWORDS.some((metro) => text.includes(metro));
};

function UserDetailsScreen({
  fullName,
  city,
  onNameChange,
  onCityChange,
  cityOptions = [],
  onBack,
  onContinue,
  title = "Tell us about you",
  subtitle = "Just a couple of details to get started",
  continueLabel = "Continue",
  isSubmitting = false,
}) {
  const [cityQuery, setCityQuery] = useState(city || "");
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const canContinue = String(fullName || "").trim() && String(city || "").trim();
  const sortedCityOptions = useMemo(
    () =>
      [...cityOptions].sort((a, b) => {
        const aMetro = isMetroCityOption(a.label);
        const bMetro = isMetroCityOption(b.label);
        if (aMetro !== bMetro) return aMetro ? -1 : 1;
        return String(a.label || "").localeCompare(String(b.label || ""), "en", {
          sensitivity: "base",
        });
      }),
    [cityOptions],
  );
  const filteredCityOptions = useMemo(() => {
    const query = String(cityQuery || "").trim().toLowerCase();
    if (!query) return sortedCityOptions;
    return sortedCityOptions.filter((option) =>
      String(option.label || "").toLowerCase().includes(query),
    );
  }, [sortedCityOptions, cityQuery]);

  useEffect(() => {
    setCityQuery(city || "");
  }, [city]);

  return (
    <div className="req-screen req-screen-user-details">
      <div className="req-content">
        <div className="req-inline-header">
          <button
            type="button"
            className="req-back-icon-btn"
            onClick={onBack}
            aria-label="Go back"
          >
            <img src="/caretLeft.svg" alt="" />
          </button>
          <p className="req-eyebrow">Manage your requirement</p>
        </div>

        <h1 className="req-verify-title">{title}</h1>
        <p className="req-verify-subtitle">{subtitle}</p>

        <div className="req-user-details-form">
          <div className="req-field-wrap">
            <label htmlFor="req-user-name" className="req-field-label">
              Full name
              <span className="req-field-required"> *</span>
            </label>
            <input
              id="req-user-name"
              type="text"
              className="req-text-input req-user-details-input"
              value={fullName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Enter your full name"
            />
          </div>

          <div className="req-field-wrap">
            <label htmlFor="req-user-city" className="req-field-label">
              City
              <span className="req-field-required"> *</span>
            </label>
            <div className="req-dropdown-field req-user-city-dropdown">
              <div className="req-dropdown-input-row">
                <img
                  src="/MapPinSimpleArea.svg"
                  alt=""
                  className="req-select-leading-icon"
                />
                <input
                  id="req-user-city"
                  type="text"
                  className={`req-select-input req-user-details-input ${!cityQuery ? "is-empty" : ""}`}
                  value={cityQuery}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setCityQuery(nextValue);
                    onCityChange("");
                    setIsCityDropdownOpen(true);
                  }}
                  onFocus={() => setIsCityDropdownOpen(true)}
                  placeholder="Search city"
                  autoComplete="off"
                />
                <button
                  type="button"
                  className={`req-select-caret-btn ${isCityDropdownOpen ? "is-open" : ""}`}
                  onClick={() => {
                    setCityQuery("");
                    onCityChange("");
                    setIsCityDropdownOpen((prev) => !prev);
                  }}
                  aria-label={isCityDropdownOpen ? "Close city options" : "Open city options"}
                >
                  <img src="/CaretRight.svg" alt="" className="req-select-caret" />
                </button>
              </div>

              {isCityDropdownOpen ? (
                <div className="req-dropdown-list">
                  {filteredCityOptions.length > 0 ? (
                    filteredCityOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`req-dropdown-option ${city === option.value ? "is-selected" : ""}`}
                        onClick={() => {
                          onCityChange(option.value);
                          setCityQuery(option.label);
                          setIsCityDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))
                  ) : (
                    <div className="req-dropdown-empty">No cities found</div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="req-footer">
        <div className="req-footer-actions req-footer-actions-select">
          <button type="button" className="req-secondary-btn" onClick={onBack}>
            Go back
          </button>
          <button
            type="button"
            className="req-primary-btn"
            data-analytics-lead-submit
            disabled={!canContinue || isSubmitting}
            onClick={onContinue}
          >
            {isSubmitting ? "Creating..." : continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserDetailsScreen;
